import { useEffect, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CallState, SAPContact, SAPContactSearchState } from '../../types';
import {
  SapContact,
  generateCallReferenceId,
  openSapContactViaCTI,
  sendSapEnd,
} from '../../services/sap-cti.service';
import { findSapContactByIdentifier } from '../../services/sap-cti/sap-contact-identity.util';
import { logCallActivity } from '../../services/call-log/call-log.service';

interface UseSapAutoOpenContactOptions {
  callState: CallState;
  isSapC4cEnabled: boolean;
  sapContactState: SAPContactSearchState;
  /** Click-to-dial SAP contact id — preferred when multiple phone matches exist. */
  preferredContactId?: string | null;
  /** Reference created when SAP initiated click-to-dial; reused for ACCEPT and END. */
  preferredReferenceId?: string | null;
}

function resolveContactName(contact: SAPContact, t: (key: string) => string): string {
  return contact.type === 'corporate'
    ? contact.accountName || t('call:sap.unknownCompany')
    : `${contact.firstName} ${contact.lastName}`.trim() || t('call:sap.unknownContact');
}

export function useSapAutoOpenContact({
  callState,
  isSapC4cEnabled,
  sapContactState,
  preferredContactId = null,
  preferredReferenceId = null,
}: UseSapAutoOpenContactOptions) {
  const { t } = useTranslation();
  const { callerNumber, contactId, status } = callState;
  const [openedSapContacts, setOpenedSapContacts] = useState<SapContact[]>([]);
  /** Tracks whether ACCEPT was already sent for the current call leg. */
  const sapAcceptSentRef = useRef(false);
  const prevCallStatusRef = useRef(status);

  // Reset ACCEPT guard when leaving an active call (not when entering active).
  useEffect(() => {
    const wasActive = prevCallStatusRef.current === 'active';
    const isActive = status === 'active';
    if (wasActive && !isActive) {
      sapAcceptSentRef.current = false;
    }
    prevCallStatusRef.current = status;
  }, [status]);

  const linkSapContact = useCallback(
    (contact: SAPContact, source: 'auto' | 'preferred') => {
      if (!isSapC4cEnabled || !callerNumber || sapAcceptSentRef.current) {
        return;
      }

      const contactPhone = contact.phone || callerNumber;
      const referenceId = preferredReferenceId || generateCallReferenceId();
      const contactName = resolveContactName(contact, t);

      sapAcceptSentRef.current = true;
      openSapContactViaCTI(
        contact.id,
        contact.displayId,
        referenceId,
        contactPhone,
        contact.type,
        callState.direction,
        contact.firstName
      );

      setOpenedSapContacts([
        {
          id: contact.id,
          displayId: contact.displayId,
          firstName: contact.firstName,
          name: contactName,
          phone: contactPhone,
          referenceId,
          type: contact.type,
          accountId: contact.accountId,
          startedAt: new Date().toISOString(),
        },
      ]);

      logCallActivity(
        'SAP_CONTACT_OPENED',
        {
          source,
          contact: {
            id: contact.id,
            name: contactName,
            phone: contactPhone,
            referenceId,
            type: contact.type,
            accountId: contact.accountId,
          },
        },
        contactId
      );
    },
    [isSapC4cEnabled, callerNumber, contactId, callState.direction, preferredReferenceId, t]
  );

  useEffect(() => {
    if (
      status !== 'active' ||
      sapContactState.contacts.length === 0 ||
      openedSapContacts.length > 0 ||
      sapAcceptSentRef.current
    ) {
      return;
    }

    // Click-to-dial: link the known contact even when the phone has multiple matches.
    if (preferredContactId) {
      const preferred = findSapContactByIdentifier(sapContactState.contacts, preferredContactId);
      if (preferred) {
        linkSapContact(preferred, 'preferred');
        return;
      }
    }

    // Unique phone match — auto-link.
    if (sapContactState.contacts.length === 1) {
      linkSapContact(sapContactState.contacts[0], 'auto');
    }
  }, [
    status,
    sapContactState.contacts,
    openedSapContacts.length,
    preferredContactId,
    linkSapContact,
  ]);

  const handleSapContactOpened = useCallback(
    (contact: SapContact) => {
      setOpenedSapContacts((prev) => {
        const previous = prev[0];
        if (previous?.id === contact.id) {
          return prev;
        }

        // Only END a prior interaction that was actually ACCEPTed to SAP.
        if (previous?.referenceId && previous.phone) {
          sendSapEnd(
            previous.phone,
            previous.id,
            previous.referenceId,
            undefined,
            previous.displayId
          );
        }

        return [contact];
      });

      sapAcceptSentRef.current = true;

      logCallActivity(
        'SAP_CONTACT_OPENED',
        {
          source: 'manual',
          contact,
        },
        contactId
      );
    },
    [contactId]
  );

  const resetOpenedContacts = useCallback(() => {
    setOpenedSapContacts([]);
    sapAcceptSentRef.current = false;
  }, []);

  return {
    openedSapContacts,
    setOpenedSapContacts,
    handleSapContactOpened,
    resetOpenedContacts,
  };
}
